<template>
  <aside :class="['sidebar', { collapsed, open: navOpen }]">

    <!-- Collapse toggle (desktop only) -->
    <button class="toggle-btn" @click="$emit('update:collapsed', !collapsed)"
            :title="collapsed ? 'Développer' : 'Réduire'">
      <span>{{ collapsed ? '›' : '‹' }}</span>
    </button>

    <!-- Logo -->
    <div class="sidebar-logo">
      <div class="logo-icon">H</div>
      <span class="logo-text">Hina<strong>Pulse</strong></span>
    </div>

    <!-- Navigation -->
    <nav class="sidebar-nav">
      <router-link
        v-for="item in menuItems"
        :key="item.path"
        :to="item.path"
        :class="['nav-item', { active: isActive(item.path) }]"
        :title="collapsed ? $t(item.label) : ''"
        @click="$emit('close')"
      >
        <span class="nav-icon">{{ item.icon }}</span>
        <span class="nav-label">{{ $t(item.label) }}</span>
      </router-link>
    </nav>

    <!-- User Info -->
    <div v-if="authStore.userEmail && !collapsed" class="sidebar-user-info">
      <div class="user-email">{{ authStore.userEmail }}</div>
    </div>

    <!-- Footer -->
    <div class="sidebar-footer">
      <button @click="toggleLanguage" class="footer-btn"
              :title="collapsed ? 'Langue' : ''">
        <span class="footer-icon">{{ language === 'fr' ? '🇫🇷' : '🇬🇧' }}</span>
        <span class="footer-label">{{ language === 'fr' ? 'FR' : 'EN' }}</span>
      </button>
      <button @click="logout" class="footer-btn logout"
              :title="collapsed ? $t('menu.logout') : ''">
        <span class="footer-icon">⏻</span>
        <span class="footer-label">{{ $t('menu.logout') }}</span>
      </button>
    </div>

  </aside>
</template>

<script setup>
import { ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '../stores/authStore'
import i18next from 'i18next'

defineProps({
  navOpen:   { type: Boolean, default: false },
  collapsed: { type: Boolean, default: false },
})
defineEmits(['update:collapsed', 'close'])

const route     = useRoute()
const router    = useRouter()
const authStore = useAuthStore()

const language = ref(i18next.language || 'fr')

const menuItems = [
  { path: '/',                  label: 'menu.home',            icon: '🏠' },
  { path: '/users',             label: 'menu.users',           icon: '👥' },
  { path: '/knowledge-base',    label: 'kb.title',             icon: '📚' },
  { path: '/videos',            label: 'lib.videos_title',     icon: '🎥' },
  { path: '/trust-passport',    label: 'menu.trust_passport',  icon: '📋' },
  { path: '/activities',        label: 'menu.activities',      icon: '📊' },
  { path: '/settings',          label: 'menu.settings',        icon: '⚙️' },
]

const isActive = (path) => route.path === path || (path !== '/' && route.path.startsWith(path))

const toggleLanguage = () => {
  const newLang = language.value === 'fr' ? 'en' : 'fr'
  language.value = newLang
  i18next.changeLanguage(newLang)
  localStorage.setItem('language', newLang)
}

const logout = async () => {
  try { await authStore.logout() } catch (_) {}
  router.push('/login')
}
</script>

<style scoped>
/* ─── Shell ─── */
.sidebar {
  width: 220px;
  min-height: 100vh;
  background: #c3d1de;
  display: flex;
  flex-direction: column;
  position: sticky;
  top: 0;
  flex-shrink: 0;
  z-index: 50;
  transition: width 0.25s cubic-bezier(.4,0,.2,1), transform 0.25s cubic-bezier(.4,0,.2,1);
  box-shadow: 2px 0 12px rgba(0,0,0,0.08);
}

.sidebar.collapsed { width: 64px; }

/* Collapsed: hide text labels, center icons */
.sidebar.collapsed .logo-text,
.sidebar.collapsed .nav-label,
.sidebar.collapsed .footer-label { display: none; }
.sidebar.collapsed .sidebar-logo { justify-content: center; padding-left: 0; padding-right: 0; }
.sidebar.collapsed .nav-item,
.sidebar.collapsed .footer-btn { justify-content: center; }

/* ─── Toggle button (desktop collapse) ─── */
.toggle-btn {
  position: absolute;
  top: 1rem;
  right: -16px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #3b82f6;
  color: #fff;
  font-size: 1.1rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 60;
  box-shadow: 0 4px 12px rgba(59,130,246,.45);
  transition: background 0.2s, transform 0.2s;
  padding: 0;
  line-height: 1;
  border: none;
  cursor: pointer;
}
.toggle-btn:hover { background: #2563eb; transform: scale(1.12); }

/* ─── Logo ─── */
.sidebar-logo {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1.1rem 1rem 0.95rem;
  border-bottom: 1px solid rgba(0,0,0,0.08);
  white-space: nowrap;
  overflow: hidden;
  min-height: 64px;
}

.logo-icon {
  min-width: 36px;
  height: 36px;
  background: linear-gradient(135deg, #3b82f6, #6366f1);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  font-weight: 900;
  color: #fff;
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(99,102,241,.35);
}

.logo-text {
  font-size: 1.05rem;
  color: #1e3a5f;
  letter-spacing: 0.01em;
}
.logo-text strong { color: #2563eb; }

/* ─── Nav ─── */
.sidebar-nav {
  flex: 1;
  padding: 1rem 0.6rem;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  overflow-y: auto;
  overflow-x: hidden;
}

.nav-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.7rem 0.75rem;
  border-radius: 0.6rem;
  color: #334155;
  font-size: 0.9rem;
  font-weight: 500;
  transition: all 0.15s ease;
  white-space: nowrap;
  text-decoration: none;
  overflow: hidden;
  min-height: 44px;
  justify-content: flex-start;
}

.nav-item:hover {
  background: rgba(59,130,246,0.12);
  color: #1e40af;
}

.nav-item.active {
  background: #3b82f6;
  color: #fff;
  font-weight: 600;
  box-shadow: 0 2px 10px rgba(59,130,246,.35);
}

.nav-icon {
  font-size: 1.15rem;
  min-width: 22px;
  text-align: center;
  flex-shrink: 0;
}
.nav-label { flex: 1; overflow: hidden; text-overflow: ellipsis; }

/* ─── User Info ─── */
.sidebar-user-info {
  padding: 0.75rem 0.6rem;
  border-top: 1px solid rgba(0,0,0,0.08);
  border-bottom: 1px solid rgba(0,0,0,0.08);
}
.user-email {
  font-size: 0.75rem;
  color: #64748b;
  word-break: break-all;
  line-height: 1.3;
}

/* ─── Footer ─── */
.sidebar-footer {
  padding: 0.75rem 0.6rem 1rem;
  border-top: 1px solid rgba(0,0,0,0.08);
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  overflow: hidden;
}

.footer-btn {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.65rem 0.75rem;
  border-radius: 0.5rem;
  background: transparent;
  color: #475569;
  font-size: 0.9rem;
  transition: all 0.15s ease;
  white-space: nowrap;
  border: none;
  cursor: pointer;
  overflow: hidden;
  min-height: 44px;
  justify-content: flex-start;
}
.footer-btn:hover { background: rgba(0,0,0,0.07); color: #1e3a5f; }
.footer-btn.logout:hover { color: #dc2626; background: rgba(220,38,38,.08); }

.footer-icon { font-size: 1.05rem; min-width: 22px; text-align: center; flex-shrink: 0; }

/* ─── Tablet + Mobile (≤1024px): off-canvas drawer ─── */
@media (max-width: 1024px) {
  .sidebar {
    position: fixed;
    left: 0;
    top: 0;
    height: 100vh;
    width: 270px;
    transform: translateX(-100%);
    box-shadow: 6px 0 28px rgba(15,23,42,0.22);
  }
  .sidebar.open { transform: translateX(0); }

  /* In the drawer we always show the full, labelled layout */
  .sidebar.collapsed { width: 270px; }
  .sidebar.collapsed .logo-text,
  .sidebar.collapsed .nav-label,
  .sidebar.collapsed .footer-label { display: inline; }
  .sidebar.collapsed .sidebar-logo { justify-content: flex-start; padding-left: 1rem; padding-right: 1rem; }
  .sidebar.collapsed .nav-item,
  .sidebar.collapsed .footer-btn { justify-content: flex-start; }

  /* The desktop collapse control is irrelevant in drawer mode */
  .toggle-btn { display: none; }
}
</style>
